<template>
    <Dialog :visible="showDialog" class="add-popup" :modal="true" :draggable="false">
        <template #container>
            <div class="dialog-header">
                Add a New User
            </div>
            <div class="dialog-content">
                <span class="text text-center">Add a new user to allow them to use some licenses you own.</span>
                <div class="flex flex-column w-full justify-content-left mt-2 mb-5">
                    <p class="input-text-label">Email</p>
                    <div class="flex flex-column w-full">
                        <InputText ref="refInputTextUser" required v-model="newUserEmail" :class="{ 'p-invalid': errorEmailMessage && isValidEmail(newUserEmail)!='success' }"/>
                        <div v-if="errorEmailMessage || showSendInvitation" class="mt-1 flex flex-column w-full">
                            <span v-if="errorEmailMessage" class="subtext" style="color: var(--inputcomponent-invalid-color);">{{ errorEmailMessage }}</span>
                            <div v-if="showSendInvitation" class="mt-3 align-self-center flex flex-column align-items-center justify-content-center text-center px-6 gap-2">
                                <span v-if="alreadyValidEmailMessage" class="subtext" style="font-style: italic; color: var(--mysuccess-color);">{{ alreadyValidEmailMessage }}</span>
                                <span v-if="successfulInvitationEmail" class="subtext" style="font-style: italic; color: var(--mysuccess-color);">If the email address exists, an invitation has been sent to <span style="font-weight: var(--font-weight-normal);">{{ successfulInvitationEmail }}</span>.</span>
                                <span v-if="errorSendInvitationMessage" class="subtext" style="font-style: italic; color: var(--inputcomponent-invalid-color);">{{ errorSendInvitationMessage }}</span>
                                <div v-if="!httpSendInvitationLoading" class="flex flex-column align-items-center justify-content-center">
                                    <span id="send-invitation-text" :class="{ 'cursor-pointer': newUserEmail, 'disabled': !newUserEmail || !isHumanVerified }" style="font-size: var(--register-subtext-font-size); font-weight: var(--register-subtext-font-weight);" @click="if (newUserEmail && isHumanVerified) { sendInvitation(false); }" v-tooltip.bottom="{ value: newUserEmail ? '' : 'Please enter an email address.', showDelay: 800 }">Send invitation{{ linkedLicenses ? " (without shared access)" : "" }} ?</span> 
                                    <span v-if="linkedLicenses" id="send-invitation-text" class="flex flex-row align-items-center justify-content-center" :class="{ 'cursor-pointer': newUserEmail, 'disabled': !newUserEmail || !isHumanVerified }" style="font-size: var(--register-subtext-font-size); font-weight: var(--register-subtext-font-weight);" @click="if (newUserEmail && isHumanVerified) { sendInvitation(true); }" v-tooltip.bottom="{ value: newUserEmail ? '' : 'Please enter an email address.', showDelay: 800 }">Send invitation (with shared access to {{ linkedLicenses.length>1 ? "" : "the " }}serial number{{ linkedLicenses.length>1 ? "s" : "" }}) ?
                                        <div class="flex flex-column align-items-center justify-content-center" v-tooltip.right="{ value: sharedAccessSerialNumbersInfoTooltip }">
                                            <SvgInfoCircle class="ml-1" className="pluscircle-svg" color="var(--button-normal-color)"/>
                                        </div>
                                    </span> 
                                </div>
                                <div v-else>
                                    <span class="text" style="opacity: 0.65;">Wait ...</span>
                                </div>
                                
                            </div>
                        </div>
                    </div>
                </div>
                <TurnstileWidget widgetId="addUserTurnstileWidget" @human-verified="(token) => {turnstileToken = token; isHumanVerified = true}"/>
            </div>
            <div class="dialog-footer">
                <div class="buttons">
                    <ButtonModule type="secondary" severity="normal" label="Cancel" @click="emit('close')"/>
                    <ButtonModule type="primary" severity="normal" :label="!httpAddUserLoading? 'Add' : 'Wait ...'" @click="clickAdd" :disabled="!isHumanVerified || !newUserEmail || httpAddUserLoading"/>
                </div>
            </div>
        </template>
    </Dialog>
</template>

<script setup>
// --- IMPORT ---
import { ref, toRefs, watchEffect, onMounted, onBeforeUnmount, computed } from 'vue';
// PrimeVue
import Dialog from 'primevue/dialog'
import InputText from 'primevue/inputtext';
// vue
import ButtonModule from '@/components/Modules/ButtonModule'
import SvgInfoCircle from '@/components/SVGs/SvgInfoCircle.vue';
import TurnstileWidget from '@/components/Modules/TurnstileWidget.vue';
// Others
import InputsUtils from '@/composables/InputsUtils'
import toastService from '@/composables/toastService';
import { useStore } from 'vuex';
import { useRouter } from 'vue-router'
import UserUtils from '@/composables/UserUtils';

const store = useStore()
const router = useRouter()
const { isValidEmail } = InputsUtils()
const { logoutUser } = UserUtils(store, router)

// API
import useApiRequest from '@/composables/HTTPS/useApiRequest';
const { fetchAddUser, fetchSendInvitation } = useApiRequest()
const httpAddUserLoading = ref(false)
const httpAddUserData = ref(null)
const httpSendInvitationLoading = ref(false)
const httpSendInvitationData = ref(null)

// --- EMITS ---
const emit = defineEmits(['close', 'added-new-user'])

// --- PROPS ---
const props = defineProps({
    showDialog: Boolean,
    linkedLicenses: {
        type: Array,
        default: null
    }
})
const { showDialog, linkedLicenses } = toRefs(props)

// --- VARIABLES ---
const newUserEmail = ref(null)
const errorEmailMessage = ref(null)
const showSendInvitation = ref(false)

// --- Validate add ---
const clickAdd = async () => {
    errorEmailMessage.value = null
    alreadyValidEmailMessage.value = null
    successfulInvitationEmail.value = null
    errorSendInvitationMessage.value = null
    showSendInvitation.value = false

    // If nothing, return
    if (!newUserEmail.value) { return }
    // Check if email is valid
    const messageValidity = isValidEmail(newUserEmail.value)
    if (messageValidity !== 'success') {
        errorEmailMessage.value = messageValidity
        return
    }
    
    // HTTP request
    httpAddUserLoading.value = true
    try {
        httpAddUserData.value = await fetchAddUser({ user_email: newUserEmail.value, cf_turnstile_response : turnstileToken.value })
        // Add the new user info in the cache
        store.commit('users/addUser', httpAddUserData.value.user_info)
        emit('added-new-user', httpAddUserData.value.user_info)
        // Toast + close the window
        //toastService.add({ severity: 'success', summary: "Add a New User: Success", detail: 'You have a new user !', life: 5000 });
        emit('close')
    } catch (err) {
        turnstileToken.value = null;
        isHumanVerified.value = false;
        window.turnstile.reset("#addUserTurnstileWidget")
        handleAddUserError(err)
    } finally {
        httpAddUserLoading.value = false
    }
}
// Handle an error from the server
const handleAddUserError = (error) => {
    try {
        if (error.response == null) { // If there is no .response key, then it means it is not an error from the server, it did not reach it
            toastService.add({ severity: 'error', summary: `Add a New User: Server not reachable.`, detail: `The server is not reachable. Please make sure you are connected to the internet. If you are, please try again.`, life: 5000 });
            return;
        }
        const errorStatus = error.response.status
        const errorCode = error.response.data.error_code
        const errorMessage = error.response.data.error_message
        switch (errorStatus) {
            case 400: // ACCESS_TOKEN_MISSING / MISSING_OBJ / MISSING_OBJ_ELEM (400)
                if (errorCode == 'ACCESS_TOKEN_MISSING') {
                    toastService.add({ severity: 'error', summary: "Add a New User: Authorization Issue.", detail: 'You are not logged in.', life: 5000 });
                    // Logout
                    logoutUser()
                }
                else if (errorCode == 'MISSING_OBJ' || errorCode == 'MISSING_OBJ_ELEM') { // MISSING_OBJ / MISSING_OBJ_ELEM
                    toastService.add({ severity: 'warn', summary: "Add a New User: Missing Information.", detail: 'It seems that the request body does not contain the necessary information.', life: 5000 });
                }
                else { toastService.add({ severity: 'error', summary: `Add a New User: Error ${errorStatus}.`, detail: errorMessage, life: 5000 }); }
                break;
            case 401: // ACCESS_TOKEN_EXPIRED / ACCESS_TOKEN_INVALID (401)
                if (errorCode == 'ACCESS_TOKEN_EXPIRED' || errorCode == 'ACCESS_TOKEN_INVALID') { // ACCESS_TOKEN_EXPIRED / ACCESS_TOKEN_INVALID
                    // Logout: Do nothing, the interceptor should have made a refresh token request!
                    // logoutUser()
                }
                else { toastService.add({ severity: 'error', summary: `Add a New User: Error ${errorStatus}.`, detail: errorMessage, life: 5000 }); }
                break;
            case 404: // ACCOUNT_NOT_FOUND / EMAIL_NOT_EXISTING (404) => toast + logout
                if (errorCode == 'ACCOUNT_NOT_FOUND') {
                    toastService.add({ severity: 'error', summary: "Add a New User: Account Not Found.", detail: 'The account no more exists.', life: 5000 });
                    // Logout
                    logoutUser()
                }
                else if (errorCode == 'EMAIL_NOT_EXISTING') { // EMAIL_NOT_EXISTING
                    errorEmailMessage.value = "The email doesn't exist, i.e. it is not linked to an existing account."
                    showSendInvitation.value = true
                }
                else { toastService.add({ severity: 'error', summary: `Add a New User: CAPTCHA Error.`, detail: "The CAPTCHA you entered is invalid or has expired. Please try again.", life: 5000 }); } // It only happens when the CAPTCHA is not correct
                break;
            case 500: // DATABASE_ERROR (500) => toast
                if (errorCode == 'DATABASE_ERROR') {
                    toastService.add({ severity: 'error', summary: "Add a New User: Internal Error.", detail: 'Sorry, it seems that there is an internal error. Try later.', life: 5000 });
                }
                else { toastService.add({ severity: 'error', summary: `Add a New User: Error ${errorStatus}.`, detail: errorMessage, life: 5000 }); }
                break;
            default:
                toastService.add({ severity: 'error', summary: `Add a New User: Error ${errorStatus}.`, detail: errorMessage, life: 5000 });
                break;
        }
    } catch {
        toastService.add({ severity: 'error', summary: `Add a New User: Server not reachable.`, detail: `The server is not reachable. Please make sure you are connected to the internet. If you are, please try again.`, life: 5000 });
    }
}

// -- Send invitation --
const sharedAccessSerialNumbersInfoTooltip = computed(() => {
    var res = "The invitation will also share access to the following product" + (linkedLicenses.value.length>1 ? "s" : "") + ":"
    linkedLicenses.value.forEach((license) => {
        res = res + "\n- " + license.serial_number + " (" + license.product.application + ")"
    })
    return res;
})
const alreadyValidEmailMessage = ref(null)
const successfulInvitationEmail = ref(null)
const errorSendInvitationMessage = ref(null)
const sendInvitation = async (withSharedAccess) => {
    errorEmailMessage.value = null
    alreadyValidEmailMessage.value = null
    successfulInvitationEmail.value = null
    errorSendInvitationMessage.value = null
    // If nothing, return
    if (!newUserEmail.value) { return }
    // Check if email is valid
    const messageValidity = isValidEmail(newUserEmail.value)
    if (messageValidity !== 'success') {
        errorEmailMessage.value = messageValidity
        return
    }

    // HTTP request
    httpSendInvitationLoading.value = true
    try {
        let temp = newUserEmail.value
        if (linkedLicenses.value && withSharedAccess) {
            httpSendInvitationData.value = await fetchSendInvitation({ user_email: temp, linked_serial_numbers: linkedLicenses.value.map((license) => license.serial_number), cf_turnstile_response : turnstileToken.value })
        } else {
            httpSendInvitationData.value = await fetchSendInvitation({ user_email: temp, cf_turnstile_response : turnstileToken.value })
        }
        successfulInvitationEmail.value = temp
    } catch (err) {
        handleSendInvitationError(err)
    } finally {
        turnstileToken.value = null;
        isHumanVerified.value = false;
        window.turnstile.reset("#addUserTurnstileWidget")
        httpSendInvitationLoading.value = false
    }
}
// Handle an error from the server
const handleSendInvitationError = (error) => {
    try {
        if (error.response == null) { // If there is no .response key, then it means it is not an error from the server, it did not reach it
            toastService.add({ severity: 'error', summary: `Send Invitation: Server not reachable.`, detail: `The server is not reachable. Please make sure you are connected to the internet. If you are, please try again.`, life: 5000 });
            return;
        }
        const errorStatus = error.response.status
        const errorCode = error.response.data.error_code
        const errorMessage = error.response.data.error_message
        switch (errorStatus) {
            case 400: // ACCESS_TOKEN_MISSING / MISSING_OBJ / MISSING_OBJ_ELEM / INPUTS_INVALID (400)
                if (errorCode == 'ACCESS_TOKEN_MISSING') {
                    toastService.add({ severity: 'error', summary: "Send Invitation: Authorization Issue.", detail: 'You are not logged in.', life: 5000 });
                    // Logout
                    logoutUser()
                }
                else if (errorCode == 'MISSING_OBJ' || errorCode == 'MISSING_OBJ_ELEM') { // MISSING_OBJ / MISSING_OBJ_ELEM
                    toastService.add({ severity: 'warn', summary: "Send Invitation: Missing Information.", detail: 'It seems that the request body does not contain the necessary information.', life: 5000 });
                }
                else if (errorCode == 'INPUTS_INVALID') { // INPUTS_INVALID
                    errorEmailMessage.value = "Please enter a valid email."
                }
                else { toastService.add({ severity: 'error', summary: `Send Invitation: Error ${errorStatus}.`, detail: errorMessage, life: 5000 }); }
                break;
            case 401: // ACCESS_TOKEN_EXPIRED / ACCESS_TOKEN_INVALID / EMAIL_EXISTS / EMAIL_SENDING_LIMIT_REACHED (401)
                if (errorCode == 'ACCESS_TOKEN_EXPIRED' || errorCode == 'ACCESS_TOKEN_INVALID') { // ACCESS_TOKEN_EXPIRED / ACCESS_TOKEN_INVALID
                    // Logout: Do nothing, the interceptor should have made a refresh token request!
                    // logoutUser()
                } else if (errorCode == 'EMAIL_EXISTS') { // EMAIL_EXISTS
                    alreadyValidEmailMessage.value = "This address email is already linked to a valid account."
                } else if (errorCode == 'EMAIL_SENDING_LIMIT_REACHED') { // EMAIL_SENDING_LIMIT_REACHED
                    errorSendInvitationMessage.value = "The invitation email has been sent very recently. Please wait a few moment before trying to send the invitation again."
                }
                else { toastService.add({ severity: 'error', summary: `Send Invitation: Error ${errorStatus}.`, detail: errorMessage, life: 5000 }); }
                break;
            case 404: //ACCOUNT_NOT_FOUND (404) => toast + logout
                if (errorCode == 'ACCOUNT_NOT_FOUND') {
                    toastService.add({ severity: 'error', summary: "Send Invitation: Account Not Found.", detail: 'The account no more exists.', life: 5000 });
                    // Logout
                    logoutUser()
                }
                else { toastService.add({ severity: 'error', summary: `Send Invitation: CAPTCHA Error.`, detail: "The CAPTCHA you entered is invalid or has expired. Please try again.", life: 5000 }); } // It only happens when the CAPTCHA is not correct
                break;
            case 500: // DATABASE_ERROR / INTERNAL_ERROR / EMAIL_ERROR (500) => toast
                if (errorCode == 'DATABASE_ERROR' || errorCode == 'INTERNAL_ERROR') {
                    toastService.add({ severity: 'error', summary: "Send Invitation: Internal Error.", detail: 'Sorry, it seems that there is an internal error. Try later.', life: 5000 });
                } else if (errorCode == 'EMAIL_ERROR') { // EMAIL_ERROR
                    toastService.add({ severity: 'error', summary: "Send Invitation: Email Sending.", detail: 'Our server did not manage sending the invitation email to the address you provided. Make sure it is a valid address email.', life: 5000 });
                }
                else { toastService.add({ severity: 'error', summary: `Send Invitation: Error ${errorStatus}.`, detail: errorMessage, life: 5000 }); }
                break;
            default:
                toastService.add({ severity: 'error', summary: `Send Invitation: Error ${errorStatus}.`, detail: errorMessage, life: 5000 });
                break;
        }
    } catch {
        toastService.add({ severity: 'error', summary: `Send Invitation: Server not reachable.`, detail: `The server is not reachable. Please make sure you are connected to the internet. If you are, please try again.`, life: 5000 });
    }
}

// --- TURNSTILE ---
const turnstileToken = ref(null)
const isHumanVerified = ref(false) // turnstile

/// --- Reset when closing ---
watchEffect(() => {
    if (!showDialog.value) { newUserEmail.value = null; errorEmailMessage.value = null; showSendInvitation.value = false; errorSendInvitationMessage.value = null; alreadyValidEmailMessage.value = null; successfulInvitationEmail.value = null; isHumanVerified.value = false; turnstileToken.value = null; }
})

// --- Focus + ENTER key listener ---
const refInputTextUser = ref(null)
watchEffect(() => {
    if (refInputTextUser.value) { refInputTextUser.value.$el.focus() }
})
onMounted(() => {
    window.addEventListener('keydown', handleKeyPressed)
})
onBeforeUnmount(() => {
    window.removeEventListener('keydown', handleKeyPressed)
}) 
const handleKeyPressed = (event) => {
    if (event.keyCode === 13) { // ENTER
        if (newUserEmail.value) { clickAdd() }
    }
}
</script>

<style scoped>
/* Send invitation */
#send-invitation-text {
    text-decoration: none;
    color: var(--button-normal-color);
    border-bottom: 1px solid transparent;
}
#send-invitation-text.disabled {
    opacity: 0.6;
}
#send-invitation-text:hover:not(.disabled) {
    border-bottom: 1px solid var(--button-normal-color);
}
</style>